import React, { useCallback, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useEventListener, lazyDocument } from '@ds/react-utils'
import { EscapeToClose } from '../../../utilities'
import {
  getBoundary,
  getInteractiveBoundaries,
  isWithinInteractiveBoundary,
} from './utils'

export interface PopoverManagerProps {
  /**
   * Dismisses the popover (sets visibility to `false`) when the user presses
   * the Escape key
   */
  dismissOnEscape?: boolean
  /**
   * Dismisses the popover (sets visibility to `false`) when the user clicks
   * outside the trigger's boundaries.
   */
  dismissOnOutsideClick?: boolean
  /**
   * Allows the user to mouse over the popover content when showOnMouseEnter is
   * active. e.g. Activating a tooltip and keeping it open while hovering its content.
   */
  interactive?: boolean
  /**
   * Callback fired when popover visibility is set to `false`.
   */
  onHide?: ({ triggerElement }: { triggerElement: HTMLElement | null }) => void
  /**
   * Callback fired when popover visibility is set to `true`.
   */
  onShow?: ({ triggerElement }: { triggerElement: HTMLElement | null }) => void
  /**
   * A function that renders a Popover.
   *
   * `hide: () => void` sets visible to false
   *
   * `popoverRef: React.Ref<HTMLElement>` a ref to be assigned to the popover's ref property.
   * For native elements this is simply `ref`, for @ds/ui elements this is typically `forwardedRef`.
   *
   * `triggerElement: HTMLElement | null` the DOM element obtained from the triggerRef (see trigger prop) that is typically used as the Popover anchorElement.
   *
   * `visible: boolean` - true/false based on specified behaviors
   */
  popover: ({
    hide,
    popoverRef,
    triggerElement,
    visible,
  }: {
    hide: () => void
    popoverRef: React.Ref<HTMLElement>
    triggerElement: HTMLElement | null
    visible: boolean
  }) => React.ReactElement
  /**
   * Shows the popover (sets `visible` to `true`) when the trigger element is focused.
   * Hides the popover (sets `visible` to `false`) when the trigger element is blurred.
   */
  showOnFocus?: boolean
  /**
   * Sets `visible` to `true` when the user is focused on the trigger and
   * presses enter.
   */
  showOnKeyboardEnter?: boolean
  /**
   * Sets `visible` to `true` when the trigger element is mouseEnter'd
   * and `false` on mouseLeave (aka 'hover').
   */
  showOnMouseEnter?: boolean
  /**
   * Toggles `visible` when the trigger element is clicked.
   */
  toggleOnClick?: boolean
  /**
   * A render function that renders the Popover's trigger.  The single
   * argument `triggerRef` is a callback ref to be assigned to the trigger's ref property.
   * For native elements this is simply `ref`, for @ds/ui elements
   * this is typically `forwardedRef`.
   *
   * The event listeners are attached to the element the ref is attached to.
   */
  trigger: ({
    triggerRef,
  }: {
    triggerRef: React.RefCallback<HTMLElement>
  }) => React.ReactElement | React.ReactElement[]
}

export function PopoverManager({
  dismissOnEscape = false,
  dismissOnOutsideClick = false,
  interactive = false,
  onHide,
  onShow,
  popover,
  showOnFocus = false,
  showOnKeyboardEnter = false,
  showOnMouseEnter = false,
  toggleOnClick = false,
  trigger,
}: PopoverManagerProps) {
  const [mouseMoveIsActive, setMouseMoveIsActive] = useState(false)
  const [visible, setVisible] = useState(false)
  const [triggerElement, setTriggerElement] = useState<HTMLElement | null>(null)
  const popoverRef = React.useRef<HTMLElement | null>(null)
  const hideTimerId = React.useRef<number | null>(null)

  const show = useCallback(() => {
    setVisible((currentValue) => {
      if (!currentValue && onShow) {
        onShow({ triggerElement })
      }
      return true
    })
  }, [onShow, triggerElement])

  const hide = useCallback(() => {
    setVisible((currentValue) => {
      if (currentValue && onHide) {
        onHide({ triggerElement })
      }

      hideTimerId.current = null
      setMouseMoveIsActive(false)

      return false
    })
  }, [onHide, triggerElement])

  const cancelScheduledHide = useCallback(() => {
    if (hideTimerId.current) {
      window.clearTimeout(hideTimerId.current)
      hideTimerId.current = null
    }
  }, [])

  // cleanup
  useEffect(() => cancelScheduledHide, [cancelScheduledHide])

  const scheduleHide = useCallback(() => {
    // scheduleHide is only called via browser
    // casting setTimeout to be browser version not node
    const timerId = window.setTimeout(() => {
      cancelScheduledHide()
      hide()
    }, 30)

    hideTimerId.current = timerId
  }, [cancelScheduledHide, hide])

  const toggle = useCallback(() => {
    setVisible((currentValue) => {
      const nextValue = !currentValue

      if (nextValue && onShow) {
        onShow({ triggerElement })
      } else if (!nextValue && onHide) {
        onHide({ triggerElement })
      }

      return nextValue
    })
  }, [onHide, onShow, triggerElement])

  const outsideClick = useCallback(
    (event) => {
      if (triggerElement && !triggerElement.contains(event.target)) {
        hide()
      }
    },
    [hide, triggerElement],
  )

  const enterHandler = useCallback(
    (event) => {
      if (['Enter'].includes(event.key)) {
        toggle()
      }
    },
    [toggle],
  )

  const mouseMoveHandler = useCallback(
    (event) => {
      // asserting that triggerElement and popoverRef.current are not null
      // as we are inside a mouse event handler
      const interactiveBoundaries = getInteractiveBoundaries(
        getBoundary(triggerElement!),
        getBoundary(popoverRef.current!),
      )

      // Note: this is intended for 'interactive' mode only
      const cursorIsInBoundary = isWithinInteractiveBoundary(
        event,
        interactiveBoundaries,
      )

      if (!cursorIsInBoundary) {
        if (!hideTimerId.current) {
          scheduleHide()
        }
      } else {
        cancelScheduledHide()
      }
    },
    [cancelScheduledHide, scheduleHide, triggerElement],
  )

  useEventListener(
    'mouseleave',
    () => {
      if (interactive) {
        setMouseMoveIsActive(true)
        scheduleHide()
      } else {
        hide()
      }
    },
    triggerElement,
    !!showOnMouseEnter && visible,
  )
  useEventListener(
    'mousemove',
    mouseMoveHandler,
    lazyDocument,
    interactive && !!showOnMouseEnter && mouseMoveIsActive,
  )
  useEventListener(
    'mouseenter',
    () => {
      setMouseMoveIsActive(false)
      show()
    },
    triggerElement,
    !!showOnMouseEnter,
  )
  useEventListener('focus', show, triggerElement, !!showOnFocus)
  useEventListener('blur', hide, triggerElement, !!showOnFocus)
  useEventListener('click', toggle, triggerElement, !!toggleOnClick)
  useEventListener('click', outsideClick, lazyDocument, !!dismissOnOutsideClick)
  useEventListener('keydown', enterHandler, lazyDocument, !!showOnKeyboardEnter)

  return (
    <EscapeToClose enabled={visible && dismissOnEscape} onClose={hide}>
      {trigger({ triggerRef: setTriggerElement })}
      {popover({
        hide,
        popoverRef,
        triggerElement,
        visible,
      })}
    </EscapeToClose>
  )
}

PopoverManager.propTypes = {
  dismissOnEscape: PropTypes.bool,
  dismissOnOutsideClick: PropTypes.bool,
  interactive: PropTypes.bool,
  onHide: PropTypes.func,
  onShow: PropTypes.func,
  popover: PropTypes.func.isRequired,
  showOnFocus: PropTypes.bool,
  showOnKeyboardEnter: PropTypes.bool,
  showOnMouseEnter: PropTypes.bool,
  toggleOnClick: PropTypes.bool,
  trigger: PropTypes.func.isRequired,
}

PopoverManager.displayName = 'PopoverManager'
